import { useEffect } from 'react';

const SITE_NAME = "Ruqyah Syar'i Tegal";
const DEFAULT_DESCRIPTION =
  "Layanan ruqyah syar'i nasional, konsultasi islami, dan artikel edukasi kesehatan ruhani sesuai Al-Qur'an & Sunnah.";
const DEFAULT_IMAGE =
  "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?auto=format&fit=crop&w=1600&q=80";

const toAbsoluteUrl = (value, siteUrl) => {
  if (!value) return '';
  if (value.startsWith('http')) return value;
  if (!siteUrl) return value;
  const trimmed = value.startsWith('/') ? value : `/${value}`;
  return `${siteUrl}${trimmed}`;
};

const upsertMeta = ({ name, property, content }) => {
  if (typeof document === 'undefined') return;
  const selector = name
    ? `meta[name="${name}"]`
    : `meta[property="${property}"]`;
  let tag = document.head.querySelector(selector);
  if (!content) {
    if (tag) tag.remove();
    return;
  }
  if (!tag) {
    tag = document.createElement('meta');
    if (name) tag.setAttribute('name', name);
    if (property) tag.setAttribute('property', property);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const upsertLink = ({ rel, href }) => {
  if (typeof document === 'undefined') return;
  const selector = `link[rel="${rel}"]`;
  let tag = document.head.querySelector(selector);
  if (!href) {
    if (tag) tag.remove();
    return;
  }
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
};

const upsertJsonLd = (jsonLd) => {
  if (typeof document === 'undefined') return;
  const existing = document.head.querySelector('script[data-seo-jsonld="true"]');
  if (!jsonLd) {
    if (existing) existing.remove();
    return;
  }
  const script = existing || document.createElement('script');
  script.setAttribute('type', 'application/ld+json');
  script.setAttribute('data-seo-jsonld', 'true');
  script.textContent = JSON.stringify(jsonLd);
  if (!existing) document.head.appendChild(script);
};

export default function Seo({
  title,
  description,
  canonical,
  image,
  type = 'website',
  noindex = false,
  keywords,
  jsonLd
}) {
  useEffect(() => {
    const siteUrl =
      (process.env.REACT_APP_SITE_URL ||
        (typeof window !== 'undefined' ? window.location.origin : '') ||
        '').replace(/\/$/, '');

    const pageUrl = canonical
      ? canonical.startsWith('http')
        ? canonical
        : `${siteUrl}${canonical.startsWith('/') ? '' : '/'}${canonical}`
      : typeof window !== 'undefined'
        ? window.location.href
        : siteUrl;

    const metaTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    const metaDescription = description || DEFAULT_DESCRIPTION;
    const metaImage = toAbsoluteUrl(image || DEFAULT_IMAGE, siteUrl);

    if (typeof document !== 'undefined') {
      document.title = metaTitle;
    }

    upsertMeta({ name: 'description', content: metaDescription });
    upsertMeta({ name: 'keywords', content: keywords });
    upsertMeta({ name: 'robots', content: noindex ? 'noindex,nofollow' : 'index,follow' });
    upsertLink({ rel: 'canonical', href: pageUrl });

    upsertMeta({ property: 'og:site_name', content: SITE_NAME });
    upsertMeta({ property: 'og:title', content: metaTitle });
    upsertMeta({ property: 'og:description', content: metaDescription });
    upsertMeta({ property: 'og:url', content: pageUrl });
    upsertMeta({ property: 'og:type', content: type });
    upsertMeta({ property: 'og:image', content: metaImage });

    upsertMeta({ name: 'twitter:card', content: metaImage ? 'summary_large_image' : 'summary' });
    upsertMeta({ name: 'twitter:title', content: metaTitle });
    upsertMeta({ name: 'twitter:description', content: metaDescription });
    upsertMeta({ name: 'twitter:image', content: metaImage });

    upsertJsonLd(jsonLd);
  }, [title, description, canonical, image, type, noindex, keywords, jsonLd]);

  return null;
}
